import { type CardWordChange, changesTouchSameWords, wordChangesOfCardOnText } from "@common/utils/redline/text/card-collisions";
import { type DiffUnitWithTrailingSeparator, WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import { TableChangePreview } from "../../../utils/anchor/proposal/table-change-preview";
import type { CommentPairingParagraphRequest } from "../../../utils/api";
import type { ActionToApply } from "../ApplyAllActionsPanel";
import type { CommentCardContent } from "../proposal-card/card-content";
import { type LivePageParagraph, type LivePageSnapshot, type PageRevisionRun, isTableGroupKey, tableGroupKeyOf } from "./page-revisions";
import { type TrackerCard, type TrackerConflictGroup, textRedlineOfTrackerCard } from "./tracker-types";

export type ParagraphMemberPlacement = "edit" | "newParagraph" | "commentOnPortion" | "commentOnParagraph";

export interface WordSpan {
  startWordIndex: number;
  endWordIndex: number;
}

export interface ParagraphGroupMember {
  action: ActionToApply;
  card: TrackerCard;
  placement: ParagraphMemberPlacement;
  position: number;
  wordSpan: WordSpan | null;
  matchedRedlineId: string | null;
  changes: CardWordChange[];
  attachedComments: ParagraphGroupMember[];
}

export interface ParagraphRedlineChanges {
  cardId: string;
  cardNumber: number;
  changes: CardWordChange[];
}

export type TrackerGroupKind = "paragraph" | "table";

export interface TrackerParagraphGroup {
  kind: TrackerGroupKind;
  bookmark: string;
  baseText: string;
  baseUnits: DiffUnitWithTrailingSeparator[];
  pageRevisions: PageRevisionRun[];
  members: ParagraphGroupMember[];
  redlineChanges: ParagraphRedlineChanges[];
  conflict: TrackerConflictGroup | null;
}

type CardParagraph = RedlineTextProposal["paragraphs"][number];

interface GroupCandidate {
  action: ActionToApply;
  card: TrackerCard;
  bookmark: string;
  paragraph: CardParagraph | null;
  baseTextHint: string | null;
}

const wordsDiffer = (left: string, right: string): boolean => !WordTokenizer.readsTheSame(left, right);

function livePageParagraphFor(bookmark: string, pageParagraphs: ReadonlyMap<string, LivePageParagraph>): LivePageParagraph | null {
  return pageParagraphs.get(bookmark) ?? null;
}

export function sectionNumberKeyOf(text: string): number[] {
  const match = /^\s*(\d+(?:\.\d+)*)/.exec(text);
  return match === null ? [] : match[1].split(".").map((part) => Number(part));
}

export function compareSectionNumberKeys(left: number[], right: number[]): number {
  if (left.length === 0 || right.length === 0) return Number(left.length === 0) - Number(right.length === 0);
  for (let position = 0; position < Math.max(left.length, right.length); position += 1) {
    const difference = (left[position] ?? -1) - (right[position] ?? -1);
    if (difference !== 0) return difference;
  }
  return 0;
}

const comparableWord = (word: string): string => WordTokenizer.contentKey(word);

function wordIndexOfPhrase(words: string[], phrase: string[]): number {
  const comparableWords = words.map(comparableWord);
  const comparablePhrase = phrase.map(comparableWord).filter((word) => word.length > 0);
  if (comparablePhrase.length === 0) return -1;
  for (let start = 0; start <= comparableWords.length - comparablePhrase.length; start += 1) {
    if (comparablePhrase.every((word, offset) => comparableWords[start + offset] === word)) return start;
  }
  return -1;
}

function changedParagraphOf(textRedline: RedlineTextProposal, bookmarkWanted: string | null): CardParagraph | null {
  if (bookmarkWanted !== null) return textRedline.paragraphs.find((paragraph) => paragraph.bookmark === bookmarkWanted) ?? null;
  return textRedline.paragraphs.find((paragraph) => paragraph.bookmark !== null && wordsDiffer(paragraph.textWhenProposed, paragraph.textProposed)) ?? null;
}

function cellBookmarksOf(card: TrackerCard): string[] {
  const content = card.content;
  if (content.kind !== "table" && content.kind !== "redline") return [];
  const paragraphBookmarks = content.textRedline?.paragraphs.flatMap((paragraph) => (paragraph.bookmark === null ? [] : [paragraph.bookmark])) ?? [];
  return Array.from(new Set([...content.bookmarks, ...paragraphBookmarks]));
}

function candidateOf(action: ActionToApply, card: TrackerCard, bookmarkWanted: string | null, tableIdByBookmark: ReadonlyMap<string, string>): GroupCandidate | null {
  const content = card.content;
  if (content.kind === "reply") return null;
  if (card.changeThroughoutSelection !== null) return null;
  if (content.kind === "table") {
    const cellBookmarks = cellBookmarksOf(card);
    const tableId = cellBookmarks.map((bookmark) => tableIdByBookmark.get(bookmark)).find((id): id is string => id !== undefined);
    return { action, card, bookmark: tableGroupKeyOf(tableId ?? cellBookmarks[0] ?? action.id), paragraph: null, baseTextHint: null };
  }
  if (content.kind === "paragraph") {
    return { action, card, bookmark: content.precedingParagraphBookmark, paragraph: null, baseTextHint: content.precedingParagraphText };
  }
  if (content.kind === "comment") {
    const bookmark = content.bookmarks[0];
    if (bookmark === undefined) return null;
    const tableId = tableIdByBookmark.get(bookmark);
    return { action, card, bookmark: tableId === undefined ? bookmark : tableGroupKeyOf(tableId), paragraph: null, baseTextHint: content.paragraphText };
  }
  if (content.textRedline === null) {
    const bookmark = content.bookmarks[0];
    return bookmark === undefined ? null : { action, card, bookmark, paragraph: null, baseTextHint: null };
  }
  const paragraph = changedParagraphOf(content.textRedline, bookmarkWanted);
  if (paragraph === null || paragraph.bookmark === null) return null;
  const tableId = tableIdByBookmark.get(paragraph.bookmark);
  if (tableId !== undefined) return { action, card, bookmark: tableGroupKeyOf(tableId), paragraph: null, baseTextHint: null };
  return { action, card, bookmark: paragraph.bookmark, paragraph, baseTextHint: paragraph.textWhenProposed };
}

function commentAnchorSpanOf(content: CommentCardContent, baseUnits: DiffUnitWithTrailingSeparator[]): WordSpan | null {
  if (content.placement === "after_paragraph" || content.anchorText === null) return null;
  const anchorUnits = WordTokenizer.diffUnits(content.anchorText).map((unit) => unit.text);
  if (anchorUnits.length === 0 || anchorUnits.length >= baseUnits.length) return null;
  const startWordIndex = wordIndexOfPhrase(baseUnits.map((unit) => unit.text), anchorUnits);
  return startWordIndex < 0 ? null : { startWordIndex, endWordIndex: startWordIndex + anchorUnits.length };
}

function editsTouchingSpan(edits: ParagraphGroupMember[], span: WordSpan): ParagraphGroupMember[] {
  const spanAsChange = { startWordIndex: span.startWordIndex, endWordIndex: span.endWordIndex, replacementWords: [] as string[] };
  return edits.filter((edit) => edit.changes.some((change) => changesTouchSameWords(change, spanAsChange)));
}

const MATCH_STOP_WORDS = new Set(["the", "a", "an", "and", "or", "of", "to", "in", "for", "on", "with", "that", "this", "is", "be", "as", "at", "by", "it", "its", "from", "should", "shall", "will", "we", "our", "your", "their", "not", "no", "are", "was", "were", "than", "then", "if", "so", "but", "any", "all", "may", "can", "which", "who", "has", "have", "had", "would", "could"]);

const NUMBER_WORDS = new Map<string, string>([
  ["one", "1"], ["two", "2"], ["three", "3"], ["four", "4"], ["five", "5"], ["six", "6"], ["seven", "7"], ["eight", "8"], ["nine", "9"], ["ten", "10"],
  ["eleven", "11"], ["twelve", "12"], ["thirteen", "13"], ["fourteen", "14"], ["fifteen", "15"], ["sixteen", "16"], ["seventeen", "17"], ["eighteen", "18"], ["nineteen", "19"],
  ["twenty", "20"], ["thirty", "30"], ["forty", "40"], ["fifty", "50"], ["sixty", "60"], ["seventy", "70"], ["eighty", "80"], ["ninety", "90"], ["hundred", "100"],
]);

const COMPOUND_NUMBER_WORDS = /\b(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[-\s](one|two|three|four|five|six|seven|eight|nine)\b/giu;

function matchTokensOf(text: string): string[] {
  const withCompoundNumbers = text.replace(COMPOUND_NUMBER_WORDS, (_match, tens: string, units: string) => String(Number(NUMBER_WORDS.get(tens.toLowerCase())) + Number(NUMBER_WORDS.get(units.toLowerCase()))));
  return withCompoundNumbers
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 0)
    .map((token) => NUMBER_WORDS.get(token) ?? token);
}

function distinctiveWordsOf(text: string): Set<string> {
  return new Set(matchTokensOf(text).filter((token) => token.length > 1 && !MATCH_STOP_WORDS.has(token)));
}

interface ScoredRedlineMatch {
  edit: ParagraphGroupMember;
  score: number;
}

function redlineMatchedByWordsOf(comment: ParagraphGroupMember, edits: ParagraphGroupMember[], baseUnits: DiffUnitWithTrailingSeparator[]): ParagraphGroupMember | null {
  if (comment.card.content.kind !== "comment") return null;
  const commentWords = distinctiveWordsOf(comment.card.content.text);
  if (commentWords.size === 0) return null;
  const baseWordSet = new Set(baseUnits.flatMap((unit) => matchTokensOf(unit.text)));
  const candidates = comment.wordSpan === null ? edits : editsTouchingSpan(edits, comment.wordSpan);
  const scored: ScoredRedlineMatch[] = candidates
    .map((edit) => {
      const introducedWords = new Set(matchTokensOf(edit.changes.flatMap((change) => change.replacementWords).join(" ")).filter((word) => !baseWordSet.has(word)));
      return { edit, score: Array.from(commentWords).filter((word) => introducedWords.has(word)).length };
    })
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score);
  if (scored.length === 0) return null;
  if (scored.length > 1 && scored[0].score === scored[1].score) return null;
  return scored[0].edit;
}

const CELL_MATCH_SCORE = 100;

function tableRedlineMatchedByCellOf(comment: ParagraphGroupMember, edits: ParagraphGroupMember[]): ParagraphGroupMember | null {
  if (comment.card.content.kind !== "comment") return null;
  const commentBookmark = comment.card.content.bookmarks[0] ?? null;
  const commentWords = distinctiveWordsOf(comment.card.content.text);
  const scored: ScoredRedlineMatch[] = edits
    .map((edit) => {
      const textRedline = textRedlineOfTrackerCard(edit.card);
      if (textRedline === null) return { edit, score: 0 };
      const editedCells = textRedline.paragraphs.filter((paragraph) => wordsDiffer(paragraph.textWhenProposed, paragraph.textProposed));
      const editsCommentCell = editedCells.some((paragraph) => paragraph.bookmark === commentBookmark);
      const introducedWords = new Set([
        ...editedCells.flatMap((paragraph) => {
          const wordsBefore = new Set(matchTokensOf(paragraph.textWhenProposed));
          return matchTokensOf(paragraph.textProposed).filter((word) => !wordsBefore.has(word));
        }),
        ...textRedline.tableChanges.rowInsertions.flatMap((insertion) => insertion.rows.flat().flatMap(matchTokensOf)),
        ...textRedline.tableChanges.tableInsertions.flatMap((insertion) => insertion.rows.flat().flatMap(matchTokensOf)),
      ]);
      const wordScore = Array.from(commentWords).filter((word) => introducedWords.has(word)).length;
      return { edit, score: (editsCommentCell ? CELL_MATCH_SCORE : 0) + wordScore };
    })
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score);
  if (scored.length === 0) return null;
  if (scored.length > 1 && scored[0].score === scored[1].score) return null;
  return scored[0].edit;
}

function tableChangeSummaryOf(textRedline: RedlineTextProposal | null): string {
  if (textRedline === null) return "";
  const sentences = TableChangePreview.lines({
    edits: textRedline.tableChanges,
    anchorTextOf: (bookmark) => textRedline.paragraphs.find((paragraph) => paragraph.bookmark === bookmark)?.textWhenProposed ?? null,
  });
  const cellChanges = textRedline.paragraphs
    .filter((paragraph) => wordsDiffer(paragraph.textWhenProposed, paragraph.textProposed))
    .map((paragraph) => `${paragraph.textWhenProposed} → ${paragraph.textProposed}`);
  return [...sentences, ...cellChanges].join(" · ");
}

export function changeSummaryOf(group: TrackerParagraphGroup, member: ParagraphGroupMember): string {
  if (member.card.content.kind === "paragraph") return `+ ${member.card.content.newParagraphTexts.join("\n")}`;
  if (group.kind === "table") return tableChangeSummaryOf(textRedlineOfTrackerCard(member.card));
  const parts = member.changes.map((change) => {
    const deletedText = group.baseText.slice(change.startOffset, change.endOffset);
    const insertedText = change.replacementText;
    if (deletedText.length > 0 && insertedText.length > 0) return `${deletedText} → ${insertedText}`;
    return deletedText.length > 0 ? `− ${deletedText}` : `+ ${insertedText}`;
  });
  return parts.length > 0 ? parts.join(" · ") : "Markup change";
}

function byPositionThenCardNumber(left: ParagraphGroupMember, right: ParagraphGroupMember): number {
  const positionOrder = left.position - right.position;
  return Number.isNaN(positionOrder) || positionOrder === 0 ? left.action.cardNumber - right.action.cardNumber : positionOrder;
}

function groupOf(bookmark: string, page: LivePageParagraph, candidates: GroupCandidate[], conflict: TrackerConflictGroup | null): TrackerParagraphGroup {
  const baseText = page.text;
  const baseUnits = WordTokenizer.diffUnitsWithTrailingSeparators(baseText);
  const editMembers: ParagraphGroupMember[] = [];
  const commentMembers: ParagraphGroupMember[] = [];
  const topLevel: ParagraphGroupMember[] = [];
  for (const candidate of candidates) {
    const { action, card } = candidate;
    const content = card.content;
    if (content.kind === "comment") {
      const span = commentAnchorSpanOf(content, baseUnits);
      commentMembers.push(span === null
        ? { action, card, placement: "commentOnParagraph", position: Number.POSITIVE_INFINITY, wordSpan: null, matchedRedlineId: null, changes: [], attachedComments: [] }
        : { action, card, placement: "commentOnPortion", position: span.startWordIndex, wordSpan: span, matchedRedlineId: null, changes: [], attachedComments: [] });
      continue;
    }
    if (content.kind === "paragraph") {
      topLevel.push({ action, card, placement: "newParagraph", position: baseUnits.length + 1, wordSpan: null, matchedRedlineId: null, changes: [], attachedComments: [] });
      continue;
    }
    const changes = candidate.paragraph === null ? [] : wordChangesOfCardOnText(candidate.paragraph, baseText);
    const member: ParagraphGroupMember = { action, card, placement: "edit", position: changes[0]?.startWordIndex ?? 0, wordSpan: null, matchedRedlineId: null, changes, attachedComments: [] };
    editMembers.push(member);
    topLevel.push(member);
  }
  for (const comment of commentMembers) {
    const ownCard = topLevel.find((member) => member.action.id === comment.card.ridesWithCardId);
    if (ownCard !== undefined) {
      comment.matchedRedlineId = ownCard.action.id;
      ownCard.attachedComments.push(comment);
      continue;
    }
    const matchedRedline = isTableGroupKey(bookmark) ? tableRedlineMatchedByCellOf(comment, editMembers) : redlineMatchedByWordsOf(comment, editMembers, baseUnits);
    if (matchedRedline !== null) {
      comment.matchedRedlineId = matchedRedline.action.id;
      matchedRedline.attachedComments.push(comment);
      continue;
    }
    const touchingEdits = comment.wordSpan === null ? [] : editsTouchingSpan(editMembers, comment.wordSpan);
    if (touchingEdits.length === 1) touchingEdits[0].attachedComments.push(comment);
    else topLevel.push(comment);
  }
  topLevel.sort(byPositionThenCardNumber);
  editMembers.forEach((edit) => edit.attachedComments.sort(byPositionThenCardNumber));
  const redlineChanges = editMembers
    .filter((edit) => !edit.action.isApplied && edit.changes.length > 0)
    .map((edit) => ({ cardId: edit.action.id, cardNumber: edit.action.cardNumber, changes: edit.changes }));
  return { kind: isTableGroupKey(bookmark) ? "table" : "paragraph", bookmark, baseText, baseUnits, pageRevisions: page.revisions, members: topLevel, redlineChanges, conflict };
}

export function paragraphGroupsOf(actions: ActionToApply[], trackerCards: Map<string, TrackerCard>, conflictGroups: TrackerConflictGroup[], page: LivePageSnapshot): TrackerParagraphGroup[] {
  const conflictKeyOf = (bookmark: string): string => {
    const tableId = page.tableIdByBookmark.get(bookmark);
    return tableId === undefined ? bookmark : tableGroupKeyOf(tableId);
  };
  const conflictByBookmark = new Map(conflictGroups.map((conflict) => [conflictKeyOf(conflict.group.bookmark), conflict]));
  const candidatesByBookmark = new Map<string, GroupCandidate[]>();
  const addCandidate = (candidate: GroupCandidate): void => {
    const candidates = candidatesByBookmark.get(candidate.bookmark) ?? [];
    if (!candidates.some((existing) => existing.action.id === candidate.action.id)) candidates.push(candidate);
    candidatesByBookmark.set(candidate.bookmark, candidates);
  };
  for (const action of actions) {
    if (action.isDismissed || action.isRemovedFromConflictGroups) continue;
    const card = trackerCards.get(action.id);
    if (card === undefined) continue;
    const candidate = candidateOf(action, card, null, page.tableIdByBookmark);
    if (candidate !== null) addCandidate(candidate);
  }
  for (const conflict of conflictGroups) {
    for (const card of conflict.cards) {
      const action = actions.find((candidate) => candidate.id === card.actionId);
      if (action === undefined || action.isDismissed) continue;
      const candidate = candidateOf(action, card, conflict.group.bookmark, page.tableIdByBookmark);
      if (candidate !== null) addCandidate(candidate);
    }
  }
  const groups: TrackerParagraphGroup[] = [];
  for (const [bookmark, candidates] of candidatesByBookmark) {
    const conflict = conflictByBookmark.get(bookmark) ?? null;
    if (candidates.length < 2 && conflict === null && !isTableGroupKey(bookmark)) continue;
    const proposedText = conflict?.group.baseText
      ?? candidates.find((candidate) => candidate.paragraph !== null)?.baseTextHint
      ?? candidates.find((candidate) => candidate.baseTextHint !== null)?.baseTextHint
      ?? "";
    const pageParagraph = livePageParagraphFor(bookmark, page.paragraphs) ?? { text: proposedText, revisions: [] };
    groups.push(groupOf(bookmark, pageParagraph, candidates, conflict));
  }
  return groups;
}

export interface CommentPairingAnswer {
  redlineByComment: ReadonlyMap<string, string | null>;
}

export interface PendingCommentPairing {
  request: CommentPairingParagraphRequest;
  redlineIdByLabel: ReadonlyMap<string, string>;
  commentIdByLabel: ReadonlyMap<string, string>;
}

const isEditMember = (member: ParagraphGroupMember): boolean => member.placement === "edit";
const isCommentCardMember = (member: ParagraphGroupMember): boolean => member.card.content.kind === "comment";
const byCardNumber = (left: ParagraphGroupMember, right: ParagraphGroupMember): number => left.action.cardNumber - right.action.cardNumber;

export function commentPairingKeyOf(group: TrackerParagraphGroup): string | null {
  const edits = allMembersOf(group).filter(isEditMember).sort(byCardNumber);
  const comments = allMembersOf(group).filter(isCommentCardMember).sort(byCardNumber);
  if (edits.length === 0 || comments.length === 0) return null;
  return JSON.stringify({
    text: group.baseText,
    edits: edits.map((edit) => [edit.action.id, changeSummaryOf(group, edit)]),
    comments: comments.map((comment) => [comment.action.id, comment.card.content.kind === "comment" ? comment.card.content.text : ""]),
  });
}

export function commentPairingRequestOf(group: TrackerParagraphGroup): PendingCommentPairing | null {
  const key = commentPairingKeyOf(group);
  if (key === null) return null;
  const edits = allMembersOf(group).filter(isEditMember).sort(byCardNumber);
  const comments = allMembersOf(group).filter(isCommentCardMember).sort(byCardNumber);
  const redlineIdByLabel = new Map(edits.map((edit, index) => [`R${index + 1}`, edit.action.id]));
  const commentIdByLabel = new Map(comments.map((comment, index) => [`C${index + 1}`, comment.action.id]));
  return {
    request: {
      paragraph_label: key,
      paragraph_text: group.baseText,
      redlines: edits.map((edit, index) => ({ redline_label: `R${index + 1}`, card_label: `#${edit.action.cardNumber}`, change: changeSummaryOf(group, edit) })),
      comments: comments.map((comment, index) => ({
        comment_label: `C${index + 1}`,
        card_label: `#${comment.action.cardNumber}`,
        text: comment.card.content.kind === "comment" ? comment.card.content.text : "",
        anchor_text: comment.card.content.kind === "comment" ? comment.card.content.anchorText : null,
      })),
    },
    redlineIdByLabel,
    commentIdByLabel,
  };
}

export function withCommentPairings(group: TrackerParagraphGroup, answer: CommentPairingAnswer | undefined): TrackerParagraphGroup {
  if (answer === undefined) return group;
  const originalParentOf = new Map<string, string>();
  for (const edit of group.members.filter(isEditMember)) {
    for (const attached of edit.attachedComments) originalParentOf.set(attached.action.id, edit.action.id);
  }
  const edits = group.members.filter(isEditMember).map((edit) => ({ ...edit, attachedComments: [] as ParagraphGroupMember[] }));
  const others = group.members.filter((member) => !isEditMember(member) && !isCommentCardMember(member));
  const topLevel: ParagraphGroupMember[] = [...edits, ...others];
  for (const original of allMembersOf(group).filter(isCommentCardMember)) {
    const redlineId = answer.redlineByComment.has(original.action.id)
      ? answer.redlineByComment.get(original.action.id) ?? null
      : originalParentOf.get(original.action.id) ?? null;
    const comment: ParagraphGroupMember = { ...original, matchedRedlineId: null, attachedComments: [] };
    const edit = redlineId === null ? undefined : edits.find((candidate) => candidate.action.id === redlineId);
    if (edit === undefined) {
      topLevel.push(comment);
      continue;
    }
    comment.matchedRedlineId = edit.action.id;
    edit.attachedComments.push(comment);
  }
  topLevel.sort(byPositionThenCardNumber);
  edits.forEach((edit) => edit.attachedComments.sort(byPositionThenCardNumber));
  return { ...group, members: topLevel };
}

export function pageBookmarksOf(group: TrackerParagraphGroup): string[] {
  if (group.kind === "paragraph") return [group.bookmark];
  return Array.from(new Set(allMembersOf(group).flatMap((member) => cellBookmarksOf(member.card))));
}

export function memberActionIdsOf(group: TrackerParagraphGroup): string[] {
  return group.members.flatMap((member) => [member.action.id, ...member.attachedComments.map((comment) => comment.action.id)]);
}

export function allMembersOf(group: TrackerParagraphGroup): ParagraphGroupMember[] {
  return group.members.flatMap((member) => [member, ...member.attachedComments]);
}
